import { NavLink, useLocation } from 'react-router-dom'
import type { ComponentType } from 'react'
import asterisk from '@/assets/asterisk.svg'
import butterfly from '@/assets/butterfly.svg'
import softStar from '@/assets/soft-star.svg'
import { cn } from '@teach/ui'
import { BAR_CLASS, useRubberBar } from '@/components/common/useRubberBar'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Картинка-значок вкладки: наши иллюстрации в миниатюре вместо линейных иконок. */
const imageIcon = (src: string) => {
  const Icon = ({ className }: { className?: string }) => (
    <img src={src} alt="" aria-hidden="true" draggable={false} className={className} />
  )
  return Icon
}

const AsteriskIcon = imageIcon(asterisk)
const ButterflyIcon = imageIcon(butterfly)
const SoftStarIcon = imageIcon(softStar)

interface Section {
  to: string
  label: 'navProfile' | 'navUsage' | 'navConnectors'
  icon: ComponentType<{ className?: string }>
}

/**
 * Разделы настроек. «Уведомления» и «Настройки» сюда НЕ попадают: за ними
 * по-прежнему ничего нет, и пункт, ведущий в пустую страницу, хуже пункта
 * «Скоро» в меню профиля.
 */
const SECTIONS: Section[] = [
  { to: '/settings/account', label: 'navProfile', icon: AsteriskIcon },
  { to: '/settings/balance', label: 'navUsage', icon: SoftStarIcon },
  { to: '/settings/connections', label: 'navConnectors', icon: ButterflyIcon },
]

export const SettingsNav = () => {
  const { pathname } = useLocation()
  const m = useMessages(messages)
  const found = SECTIONS.findIndex((section) => pathname.startsWith(section.to))
  const active = found === -1 ? 0 : found
  const { navRef, itemRef, barStyle } = useRubberBar(active)

  return (
    <nav
      ref={navRef}
      // На телефоне три вкладки влезают только без лишних отступов; полоса прокрутки
      // (если язык длиннее) спрятана — на Android она рисовалась серой линией.
      className="relative flex gap-0 overflow-x-auto border-b border-border [scrollbar-width:none] sm:gap-1 [&::-webkit-scrollbar]:hidden"
    >
      {SECTIONS.map(({ to, label, icon: Icon }, index) => (
        <NavLink
          key={to}
          to={to}
          ref={itemRef(index)}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-1.5 whitespace-nowrap px-2 py-2.5 text-sm transition-colors sm:gap-2 sm:px-3',
              isActive
                ? 'font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          <Icon className="size-4" />
          {m[label]}
        </NavLink>
      ))}
      {barStyle ? <span aria-hidden="true" className={BAR_CLASS} style={barStyle} /> : null}
    </nav>
  )
}
